import { AppDate } from '@macs-frontend/core/utils/date';
import { AppDateTime } from '@macs-frontend/core/utils/datetime';
import { UUID } from '@macs-frontend/core/utils/uuid';
import { EstimateFlowData } from '@macs-frontend/features/company-management/financial/classes/estimate-flow';
import { InvoiceFlowData } from '@macs-frontend/features/company-management/financial/classes/invoice-flow';
import { Contact } from '@macs-frontend/features/shared';
import { InvoiceEvent, InvoicePermission, UserActivityAction } from '@macs-gql';
import { BaseClient, Client } from './client';
import { BaseCompany, Company } from './company.model';
import { Currency } from './currency.model';
import { InvoiceItemGroup } from './invoice-item-group.model';
import { InvoiceItem } from './invoice-item.model';
import { BaseProject } from './project';
import { BaseRatesheet } from './ratesheet.model';
import { UploadedFile } from './uploaded-file';
import { BaseUser } from './user.model';
import { TeamCost } from '@macs-frontend/features/company-management/team-cost/team-cost.types';
import { Event } from '@macs-frontend/features/shared/models/event.model';

export interface BaseInvoice {
  uuid: UUID;
  subject: string;
  invoiceId: string;
  codePO: string;
  issueDate: AppDate;
  dueDate: AppDate;
  paidAt: AppDateTime;
  tax: number;
  subtotal?: number;
  amountPaid: number;
  createdAt: AppDateTime;
  lastEditedAt: AppDateTime;
  noteReviewer: string;
  discount: number;
  discountAmount: number;
  discountSum: number;
  hideDiscountPercent: boolean;
  total: number;
  totalCost: number;
  status: InvoiceStatuses;
  extendedStatus: InvoiceStatuses;
  isMain: boolean;
  type: InvoiceTypes;
  dueDateType: string;
  price?: number;
  notes: string;
  notesForClient: string;
  token?: string;
  paymentReference?: string;
  template?: boolean;
  manuallySaved?: boolean;
  invoicedAmount: number;
  checkedForBt: boolean;
  budgetTimingAmount: number;
  permissions?: Record<InvoicePermission, boolean>;
  statusChangedByUser: BaseUser | null;
  statusChangedByContact: Contact | null;
  statusChangedAt: AppDateTime | null;
  statusNote: string;
  declineReason: null | InvoiceDeclineReason;
  showEstimateInvoicedAmount: boolean;
  snapshot: boolean;
  snapshotSourceUuid: UUID | null;
  lastEvents: InvoiceLastEvents | null;
  sentJournal: Event[];
  leadNames: string | null;
  reviewRequester: BaseUser;
}

export interface InvoiceLastEvents {
  created: Event | null;
  edited: Event | null;
  approved: Event | null;
  sentForReview: Event | null;
  denied: Event | null;
  sentToClient: Event | null;
  confirmedByClient: Event | null;
  declinedByClient: Event | null;
  requestSubmitted: Event | null;
  confirmedInternally: Event | null;
  declinedInternally: Event | null;
}

export enum InvoiceDeclineReason {
  NoGo = 'NO_GO',
  Lost = 'LOST',
}

export interface InvoiceProject extends BaseProject {
  client: BaseClient;
  ratesheet: BaseRatesheet | null;
}

export interface InvoiceClient
  extends BaseClient,
    Pick<Client, 'currency' | 'city' | 'country' | 'phones'> {}

export interface InvoiceCompany
  extends BaseCompany,
    Pick<Company, 'city' | 'country' | 'phones' | 'state'> {}

export interface InvoicePayment {
  uuid: UUID;
  date: string;
  amount: number;
  description: string;
}

export interface Invoice extends BaseInvoice {
  invoicePayments: InvoicePayment[];
  invoiceGroups: InvoiceItemGroup[];
  invoiceItems: InvoiceItem[];
  teamCosts: TeamCost[];
  items?: (InvoiceItem | InvoiceItemGroup)[];
  ratesheet?: BaseRatesheet;
  company: InvoiceCompany;
  projects?: InvoiceProject[];
  creator: BaseUser;
  reviewer: BaseUser;
  files: UploadedFile[];
  currency: Currency;
  project?: InvoiceProject;
  client?: InvoiceClient;
  isBudgetTimingFilled?: boolean;
  canSendForReview: boolean;
  lastEditedAt: AppDateTime;
  currentEditor: BaseUser | null;
}

export enum InvoiceTypes {
  INVOICE = 'INVOICE',
  ESTIMATE = 'ESTIMATE',
}

export interface InvoicesCount {
  count: number;
  status: string;
}

export interface InvoicesCountByStatus<T> {
  [key: string]: T;
}

export enum InvoiceItems {
  INVOICE_ITEM = 'INVOICE_ITEM',
  INVOICE_GROUP = 'INVOICE_GROUP',
}

export enum InvoiceGroupType {
  EXPENSES = 'EXPENSES',
  TASKS = 'TASKS',
  TEAM_COST = 'TEAM_COST',
  MATERIAL_COST = 'MATERIAL_COST',
  STAFFING_COST = 'STAFFING_COST',
  FEES = 'FEES',
  WAREHOUSES = 'WAREHOUSES',
  BUDGET_SPLIT = 'BUDGET_SPLIT',
  CUSTOM = 'CUSTOM',
}

export enum InvoiceStatuses {
  DRAFT = 'DRAFT',
  ARCHIVED = 'ARCHIVED',
  PAID = 'PAID',
  SENT = 'SENT',
  IN_REVIEW = 'IN_REVIEW',
  APPROVED = 'APPROVED',
  CONFIRMED = 'CONFIRMED',
  DECLINED = 'DECLINED',
  DENIED = 'DENIED',
  CPO_RECEIVED = 'CPO_RECEIVED',
  INVOICE_REQUEST = 'INVOICE_REQUEST',
  COMPLETED = 'COMPLETED',
  REJECTED_BY_CLIENT = 'REJECTED_BY_CLIENT',
  /**
   * extended statuses
   */
  LATE = 'LATE',
  RESENT = 'RESENT',
  EXECUTED = 'EXECUTED',
  /**
   * statuses for front
   */
  ALL = 'ALL',
}

export const ActiveInvoiceStatuses: InvoiceStatuses[] = Object.values(
  InvoiceStatuses,
).filter(status => status !== InvoiceStatuses.ARCHIVED && status !== InvoiceStatuses.ALL);

export function countActiveInvoices(
  totalItems: number,
  countsByStatus: { [x: string]: { count: number } },
): number {
  return totalItems - (countsByStatus[InvoiceStatuses.ARCHIVED]?.count || 0);
}

export const activeTabExportUnavailableTitle =
  'Export is not available on the Active tab. Use the All tab to export.';

export const InvoiceExtendedStatuses = Object.freeze({
  [InvoiceStatuses.LATE]: InvoiceStatuses.LATE,
  [InvoiceStatuses.RESENT]: InvoiceStatuses.RESENT,
  [InvoiceStatuses.EXECUTED]: InvoiceStatuses.EXECUTED,
});

export const estimateBudgetRelatedStatuses = [
  InvoiceStatuses.CONFIRMED,
  InvoiceStatuses.SENT,
  InvoiceStatuses.CPO_RECEIVED,
];

export enum InvoiceStatusStyles {
  DRAFT = 'bordered',
  ARCHIVED = 'bordered',
  SENT = 'purple',
  RESENT = 'purple',
  PAID = 'green',
  LATE = 'red',
  CONFIRMED = 'green',
  APPROVED = 'green',
  DENIED = 'red',
  IN_REVIEW = 'yellow',
  DECLINED = 'red',
  CPO_RECEIVED = 'green',
  INVOICE_REQUEST = 'bordered',
  COMPLETED = 'green',
  REJECTED_BY_CLIENT = 'red',
  EXECUTED = 'bordered',
}

//export type InvoiceOrEstimateWithFlow = Invoice & { flow: InvoiceFlowData | EstimateFlowData };
export type InvoiceOrEstimateWithFlow = InvoiceWithFlow | EstimateWithFlow;

export type InvoiceWithFlow = Omit<Invoice, 'type'> & {
  type: InvoiceTypes.INVOICE;
  flow: InvoiceFlowData;
};
export type EstimateWithFlow = Omit<Invoice, 'type'> & {
  type: InvoiceTypes.ESTIMATE;
  flow: EstimateFlowData;
};
